/**
 * Delade regler för när AI-chatt, banners och liknande element får visas.
 * Sökvägsmönster stöder exakt match, prefix via avslutande "*" och "/" = startsidan.
 */
export type DeviceRule = "all" | "mobile" | "desktop";

export const DEVICE_RULES: { value: DeviceRule; label: string }[] = [
  { value: "all", label: "Alla enheter" },
  { value: "mobile", label: "Endast mobil" },
  { value: "desktop", label: "Endast dator" },
];

/** Delar upp en textarea/kommaseparerad lista i rena sökvägsmönster. */
export const parsePathList = (value: string): string[] =>
  value
    .split(/[\n,]/)
    .map((p) => p.trim())
    .filter(Boolean);

export const formatPathList = (paths: string[] | null | undefined): string =>
  (paths ?? []).join("\n");

const normalize = (path: string) => {
  const trimmed = path.trim().toLowerCase();
  if (!trimmed.startsWith("/")) return `/${trimmed}`;
  return trimmed.length > 1 && trimmed.endsWith("/") ? trimmed.slice(0, -1) : trimmed;
};

/** Matchar en sökväg mot ett mönster ("/elpriser", "/elpriser/*", "*"). */
export const matchesPath = (pathname: string, pattern: string): boolean => {
  const path = normalize(pathname);
  const raw = pattern.trim().toLowerCase();
  if (!raw) return false;
  if (raw === "*") return true;
  if (raw.endsWith("*")) {
    const prefix = normalize(raw.slice(0, -1) || "/");
    return path === prefix || path.startsWith(prefix === "/" ? "/" : `${prefix}/`);
  }
  return path === normalize(raw);
};

export const matchesAnyPath = (pathname: string, patterns: string[] | null | undefined): boolean =>
  (patterns ?? []).some((p) => matchesPath(pathname, p));

export type DisplayRules = {
  /** Tomt = visa på alla sidor */
  includePaths?: string[] | null;
  excludePaths?: string[] | null;
  device?: DeviceRule | null;
};

/** true om elementet får visas på given sökväg och enhet. */
export const passesDisplayRules = (
  rules: DisplayRules,
  pathname: string,
  isMobile: boolean,
): boolean => {
  const device = rules.device ?? "all";
  if (device === "mobile" && !isMobile) return false;
  if (device === "desktop" && isMobile) return false;
  if (matchesAnyPath(pathname, rules.excludePaths)) return false;
  const include = rules.includePaths ?? [];
  if (include.length > 0 && !matchesAnyPath(pathname, include)) return false;
  return true;
};
